import { HttpParams } from '@angular/common/http';

// Models + query-param builders for the reporting distributions dashboard endpoints:
//   GET reporting/distributionsDashboard
//   GET reporting/distribution/{distributionOid}/respondents
// These replace the per-survey fan-out that previously issued one request per survey.

export type DistributionDashboardStatus = 'archived' | 'draft' | 'closed' | 'active' | 'scheduled';

export interface DistributionRespondentsInfo {
  totalCount: number;
  completed: number;
  completedPercentage: number;
  reportsSent: number;
}

export interface DistributionDashboardApiModel {
  idobject: number;
  name: string;
  // Combined "Survey / Version" string.
  measure: string;
  organizationName: string;
  distributionTypeName: string;
  individualReport: boolean;
  archive: boolean;
  respondentsInfo?: DistributionRespondentsInfo;
  status: DistributionDashboardStatus;
  // Auto-initialized on the backend, immutable. `createrBy` keeps the backend's spelling.
  createdAt?: string | null;
  createrBy?: string | null;
}

export type DistributionsDashboardSortColumn =
  | 'name'
  | 'measure'
  | 'organizationName'
  | 'distributionTypeName'
  | 'status'
  | 'totalCount'
  | 'completed'
  | 'reportsSent'
  | 'createdAt'
  | 'createrBy';

export interface DistributionsDashboardParams {
  offsetRows?: number;
  pageSize?: number;
  sortColumnName?: DistributionsDashboardSortColumn;
  descSorting?: boolean;
  // Filter by the individual-report flag (true/false; omit for all).
  individualReport?: boolean;
  surveyVersionOid?: number;
  searchText?: string;
  includeArchive?: boolean;
}

export function buildDistributionsDashboardParams(params?: DistributionsDashboardParams): HttpParams {
  let httpParams = new HttpParams();
  if (!params) {
    return httpParams;
  }
  if (params.offsetRows != null) httpParams = httpParams.set('offsetRows', String(params.offsetRows));
  if (params.pageSize != null) httpParams = httpParams.set('pageSize', String(params.pageSize));
  if (params.sortColumnName) httpParams = httpParams.set('sortColumnName', params.sortColumnName);
  if (params.descSorting != null) httpParams = httpParams.set('descSorting', String(params.descSorting));
  if (params.individualReport != null) httpParams = httpParams.set('individualReport', String(params.individualReport));
  if (params.surveyVersionOid != null) httpParams = httpParams.set('surveyVersionOid', String(params.surveyVersionOid));
  if (params.searchText) httpParams = httpParams.set('searchText', params.searchText);
  if (params.includeArchive != null) httpParams = httpParams.set('includeArchive', String(params.includeArchive));
  return httpParams;
}

export type DistributionRespondentStatus =
  | 'reportViewed'
  | 'reportSent'
  | 'surveyCompleted'
  | 'inProgress'
  // Computed by the backend from the respondent's agreementStatus: they refused the consent form.
  | 'declined'
  | 'reminded'
  | 'invited'
  | 'noEmail'
  | 'pending';

export type DistributionDeliveryStage = 'invitation' | 'reminder' | 'report';

export interface DistributionRespondentDeliveryError {
  stage: DistributionDeliveryStage;
  at: string;
  message: string;
}

export interface DistributionRespondentApiModel {
  idobject: number;
  // Combined FirstName + LastName.
  name: string;
  email: string;
  participantGroupName: string;
  status: DistributionRespondentStatus;
  hasDeliveryError: boolean;
  deliveryError: DistributionRespondentDeliveryError | null;
}

export type DistributionRespondentsSortColumn = 'name' | 'email' | 'participantGroupName' | 'status';

export interface DistributionRespondentsParams {
  offsetRows?: number;
  pageSize?: number;
  sortColumnName?: DistributionRespondentsSortColumn;
  descSorting?: boolean;
  searchText?: string;
  status?: DistributionRespondentStatus;
}

export function buildDistributionRespondentsParams(params?: DistributionRespondentsParams): HttpParams {
  let httpParams = new HttpParams();
  if (!params) {
    return httpParams;
  }
  if (params.offsetRows != null) httpParams = httpParams.set('offsetRows', String(params.offsetRows));
  if (params.pageSize != null) httpParams = httpParams.set('pageSize', String(params.pageSize));
  if (params.sortColumnName) httpParams = httpParams.set('sortColumnName', params.sortColumnName);
  if (params.descSorting != null) httpParams = httpParams.set('descSorting', String(params.descSorting));
  if (params.searchText) httpParams = httpParams.set('searchText', params.searchText);
  if (params.status) httpParams = httpParams.set('status', params.status);
  return httpParams;
}
